//src/components/shared/ModelImportDialog.tsx
import { useMemo, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { TemplateItem } from "@/services/modelImports";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  templates: TemplateItem[];
  loading?: boolean;
  importing?: boolean;
  onImport: (ids: string[]) => Promise<void> | void;
};

export function ModelImportDialog({
  open,
  onOpenChange,
  title,
  description,
  templates,
  loading = false,
  importing = false,
  onImport,
}: Props) {
  const availableTemplates = useMemo(
    () => templates.filter((item) => !item.already_exists && item.is_active),
    [templates],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  function toggle(id: string, checked: boolean) {
    setSelectedIds((current) => checked ? [...current, id] : current.filter((item) => item !== id));
  }

  function selectAll() {
    setSelectedIds(availableTemplates.map((item) => item.id));
  }

  function clear() {
    setSelectedIds([]);
  }

  async function handleImport() {
    await onImport(selectedIds);
    setSelectedIds([]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span>Duplicados aparecem bloqueados para evitar cadastros repetidos no cliente selecionado.</span>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={selectAll} disabled={availableTemplates.length === 0}>Selecionar disponíveis</Button>
              <Button type="button" variant="ghost" size="sm" onClick={clear}>Limpar</Button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-auto rounded-lg border">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando modelos...
              </div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Nenhum modelo disponível.</div>
            ) : (
              templates.map((template) => {
                const disabled = !!template.already_exists || !template.is_active;
                return (
                  <label key={template.id} className="flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-b-0">
                    <Checkbox
                      checked={selectedIds.includes(template.id)}
                      disabled={disabled}
                      onCheckedChange={(checked) => toggle(template.id, checked === true)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{template.name}</div>
                      {template.already_exists ? (
                        <div className="text-xs text-muted-foreground">Já existe neste cliente.</div>
                      ) : null}
                    </div>
                    {template.already_exists ? <Badge variant="secondary">Duplicado</Badge> : null}
                    {!template.is_active ? <Badge variant="outline">Inativo</Badge> : null}
                    {!disabled ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : null}
                  </label>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={handleImport} disabled={selectedIds.length === 0 || importing}>
            {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Importar selecionados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
